import { useNavigate, useParams } from 'react-router-dom';
import { Terminal, Cpu, ArrowLeftRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Button, LinkCard } from '@vnetwork/ui/primitives';
import { SplitView, Section } from '@vnetwork/ui/layout';
import type { VmwareInstance } from '@/api/vmware/instances';
import { useInstanceSummary } from '@/api/vmware/instances';
import { copyToClipboard, pickPublicIpv4, allIps } from '../shared';
import { DescList, DescItem, monoValue } from '../../components/DescList';
import { CompactStat, CompactStatGroup } from '../../components/CompactStat';

/**
 * VMware instance overview — mirrors @console ManageOverview/index.tsx field set.
 *
 * BUG FIXES applied here:
 *
 * 1. Power state (console button disabled):
 *    Was: `instance.status === 'POWERED_ON'` → always wrong ("active" is provisioning)
 *    Now: `instance.metadata?.power_state === 'POWERED_ON'` (mirrors @console L783)
 *
 * 2. Overview field paths (wrong guessed shape → real vSphere shape):
 *    CPU:    metadata.resources.cpu       → metadata.cpu.count
 *    Memory: metadata.resources.memory    → metadata.memory.size_MiB (÷1024 = GiB)
 *    Disks:  metadata.disks[].capacity_gb → metadata.disks[].value.capacity (bytes ÷1024³)
 *            metadata.disks[].datastore   → metadata.disks[].value.backing.vmdk_file
 *            metadata.disks[].thin_prov.  → metadata.disks[].value.type
 *    NICs:   metadata.nics[].ip           → (none in detail; use summaryItems ipAddressLabel)
 *            metadata.nics[].mac          → metadata.nics[].value.mac_address
 *            metadata.nics[].network      → metadata.nics[].value.backing.network_name
 *            metadata.nics[].connected    → metadata.nics[].value.start_connected
 *    GuestOS key:  metadata.guest_os      → metadata.guest_OS  (capital OS)
 *
 * 3. IP addresses:
 *    Was: instance.ip (not present on list/detail rows)
 *    Now: summary.summaryItems[ipAddressLabel] — first public IPv4 as primary,
 *         all non-link-local IPs in the Connection rail (mirrors @console Networks L534).
 */
export function OverviewTab({ serviceId, instance }: { serviceId: string; instance: VmwareInstance }) {
  const navigate = useNavigate();
  const { instanceId } = useParams<{ instanceId: string }>();

  const { data: summary } = useInstanceSummary(serviceId, instanceId);

  // ---- Power state (real field) ------------------------------------------------
  // metadata.power_state = "POWERED_ON" | "POWERED_OFF" | "SUSPENDED"
  const powerState = instance.metadata?.power_state;
  const poweredOn = powerState === 'POWERED_ON';

  // ---- CPU / Memory from real paths -------------------------------------------
  const cpuCount = instance.metadata?.cpu?.count;
  const cpuCoresPerSocket = instance.metadata?.cpu?.cores_per_socket;
  const cpuHotAdd = instance.metadata?.cpu?.hot_add_enabled;
  const memMiB = instance.metadata?.memory?.size_MiB;
  const memGiB = memMiB != null ? Math.round(memMiB / 1024) : undefined;
  const memHotAdd = instance.metadata?.memory?.hot_add_enabled;

  // ---- Disks from real paths --------------------------------------------------
  // metadata.disks = [{ key, value: { label, type, capacity (bytes), backing: { vmdk_file } } }]
  const disks = instance.metadata?.disks ?? [];

  // ---- NICs from real paths ---------------------------------------------------
  // metadata.nics = [{ key, value: { label, mac_address, backing: { network_name }, start_connected } }]
  const nics = instance.metadata?.nics ?? [];

  // ---- Guest OS (capital OS key) ----------------------------------------------
  // metadata.guest_OS (e.g. "RHEL_7_64") — raw enum; human name from summaryItems.
  const guestOsEnum = instance.metadata?.guest_OS;

  // ---- Info from summaryItems (mirrors @console ManageOverview L762-763) ------
  const guestOsFullName =
    summary?.summaryItems?.find((x) => x.label === 'summaryView.guestFullName')?.value as string | undefined ??
    guestOsEnum;
  const hostnameRaw = summary?.summaryItems?.find((x) => x.label === 'summaryView.dnsNameLabel')?.value;
  const hostnameStr = Array.isArray(hostnameRaw) ? hostnameRaw[0] : (typeof hostnameRaw === 'string' ? hostnameRaw : undefined);

  // ---- IPs from summaryItems --------------------------------------------------
  // summaryView.ipAddressLabel is an array e.g. ["103.151.240.76","176.99.3.34","fe80::..."]
  const primaryIp = pickPublicIpv4(summary);
  const allIpList = allIps(summary);

  // ---- Timestamps from instance top-level (not in metadata) -------------------
  const createdAt = (instance as Record<string, unknown>).service_created_at as string | undefined
    ?? (instance as Record<string, unknown>).created_at as string | undefined;
  const updatedAt = (instance as Record<string, unknown>).service_updated_at as string | undefined
    ?? (instance as Record<string, unknown>).updated_at as string | undefined;

  const base = `/services/compute/${serviceId}/instances/${instanceId}`;
  const openConsole = () => window.open(`${base}/console`, instance.id, 'height=840,width=1250');
  const go = (path: string) => (e: React.MouseEvent) => { e.preventDefault(); navigate(path); };

  const rail = (
    <>
      <Card flat>
        <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-md)' }}>Connection</CardTitle></CardHeader>
        <CardBody>
          <DescList minColumn={999} style={{ gap: 'var(--vn-space-3)' }}>
            {allIpList.length > 0 ? (
              allIpList.map((ip, i) => (
                <DescItem key={ip} label={i === 0 ? 'Primary IP' : `IP ${i + 1}`}>
                  <CopyableCode value={ip} />
                </DescItem>
              ))
            ) : (
              <DescItem label="IP">—</DescItem>
            )}
            <DescItem label="Hostname">{hostnameStr || '—'}</DescItem>
          </DescList>
        </CardBody>
      </Card>

      <Section title="Quick actions">
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {/* Console link — only navigable when POWERED_ON (mirrors @console ManageOverview L783).
              LinkCard has no disabled prop; we suppress the click when off. */}
          <LinkCard
            icon={<Terminal size={16} />}
            title="Console"
            description={poweredOn ? 'Open a remote console session' : 'VM must be powered on to open console'}
            href={`${base}/console`}
            onClick={(e) => {
              e.preventDefault();
              if (poweredOn) openConsole();
            }}
            style={!poweredOn ? { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
          />
          <LinkCard
            icon={<Cpu size={16} />}
            title="Resize"
            description="Change vCPU, memory or disk"
            href={`${base}/resize`}
            onClick={go(`${base}/resize`)}
          />
          <LinkCard
            icon={<ArrowLeftRight size={16} />}
            title="Network traffic"
            description="sFlow bandwidth &amp; top talkers"
            href={`${base}/network-traffic`}
            onClick={go(`${base}/network-traffic`)}
          />
        </div>
      </Section>
    </>
  );

  return (
    <SplitView asideWidth="300px" aside={rail}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* Resource specs at a glance — real paths */}
        <Card flat>
          <CardBody>
            <CompactStatGroup columns={3}>
              <CompactStat label="vCPU" value={cpuCount ?? '—'} />
              <CompactStat label="Memory" value={memGiB != null ? `${memGiB} GiB` : '—'} />
              <CompactStat
                label="Disk"
                value={
                  disks.length > 0
                    ? `${disks.reduce((sum, d) => sum + Math.round((d.value?.capacity ?? 0) / 1024 ** 3), 0)} GB`
                    : '—'
                }
              />
            </CompactStatGroup>
          </CardBody>
        </Card>

        {/* Instance information — mirrors @console ManageOverview Instance Information card */}
        <Card>
          <CardHeader
            actions={
              /* Open console — disabled unless metadata.power_state === "POWERED_ON" */
              <Button
                size="sm"
                variant="secondary"
                startIcon={<Terminal size={15} />}
                disabled={!poweredOn}
                onClick={openConsole}
              >
                Open console
              </Button>
            }
          >
            <CardTitle>Instance information</CardTitle>
          </CardHeader>
          <CardBody>
            <DescList>
              <DescItem label="Instance ID">
                {/* Detail rows carry `service_uuid` (not `id`); the route param IS the
                    service_uuid, so use it as the reliable Instance ID source. */}
                <code style={monoValue}>
                  {String(instanceId ?? (instance as Record<string, unknown>).service_uuid ?? instance.id ?? '')}
                </code>
              </DescItem>
              <DescItem label="Machine type">
                {cpuCount != null && memGiB != null
                  ? `(${cpuCount} vCPUs${cpuCoresPerSocket != null ? `, ${cpuCoresPerSocket} cores/socket` : ''}, ${memGiB} GiB memory)`
                  : '—'}
              </DescItem>
              <DescItem label="Guest OS">{typeof guestOsFullName === 'string' ? guestOsFullName : '—'}</DescItem>
              {hostnameStr && <DescItem label="Hostname">{hostnameStr}</DescItem>}
              {primaryIp && (
                <DescItem label="Primary IP">
                  <CopyableCode value={primaryIp} />
                </DescItem>
              )}
              {createdAt && (
                <DescItem label="Creation time">
                  {new Date(createdAt).toLocaleString()}
                </DescItem>
              )}
              {updatedAt && (
                <DescItem label="Last modified">
                  {new Date(updatedAt).toLocaleString()}
                </DescItem>
              )}
            </DescList>

            {/* Hot-plug flags */}
            {(cpuHotAdd != null || memHotAdd != null) && (
              <div style={{ marginTop: 'var(--vn-space-3)', padding: 'var(--vn-space-3)', background: 'var(--vn-bg-subtle)', borderRadius: 'var(--vn-radius-sm)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                <div style={{ fontWeight: 600, marginBottom: 'var(--vn-space-1)', color: 'var(--vn-fg-default)' }}>
                  Hot-plug support
                </div>
                <div>CPU Hot Plug: <strong>{cpuHotAdd ? 'Enabled' : 'Disabled'}</strong></div>
                <div>Memory Hot Plug: <strong>{memHotAdd ? 'Enabled' : 'Disabled'}</strong></div>
                {(!cpuHotAdd || !memHotAdd) && (
                  <div style={{ marginTop: 'var(--vn-space-1)', color: 'var(--vn-warning-text, var(--vn-fg-muted))' }}>
                    Resources without Hot Plug enabled require a power-off to resize.
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* NICs — real path: nics[].value.{mac_address, backing.network_name, start_connected} */}
        {nics.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Network interfaces</CardTitle></CardHeader>
            <CardBody>
              <DescList>
                {nics.map((n, i) => {
                  const networkName = n.value?.backing?.network_name ?? `NIC ${i + 1}`;
                  const mac = n.value?.mac_address;
                  const connected = n.value?.start_connected;
                  const label = n.value?.label;
                  return (
                    <DescItem key={String(n.key)} label={networkName}>
                      <div>
                        {label && (
                          <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginBottom: 2 }}>
                            {label}
                          </div>
                        )}
                        {mac && (
                          <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)' }}>
                            MAC: {mac}
                          </div>
                        )}
                        <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)', marginTop: 2 }}>
                          Type: {n.value?.type ?? 'Standard'}
                        </div>
                        {connected != null && (
                          <div style={{ fontSize: 'var(--vn-font-size-xs)', color: connected ? 'var(--vn-success-text)' : 'var(--vn-fg-muted)', marginTop: 2 }}>
                            {connected ? 'Connected' : 'Disconnected'}
                          </div>
                        )}
                      </div>
                    </DescItem>
                  );
                })}
              </DescList>
            </CardBody>
          </Card>
        )}

        {/* Disks — real path: disks[].value.{label, type, capacity (bytes), backing.vmdk_file} */}
        {disks.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Disks</CardTitle></CardHeader>
            <CardBody>
              <DescList>
                {disks
                  .slice()
                  .sort((a, b) => Number(a.key) - Number(b.key))
                  .map((d, i) => {
                    const v = d.value;
                    const capacityGb = v?.capacity != null ? Math.round(v.capacity / 1024 ** 3) : undefined;
                    return (
                      <DescItem key={String(d.key)} label={v?.label ?? `disk-${i}`}>
                        <div>
                          <div>{capacityGb != null ? `${capacityGb} GB` : '—'}</div>
                          <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)', marginTop: 2 }}>
                            Type: {v?.type ?? '—'}
                          </div>
                        </div>
                      </DescItem>
                    );
                  })}
              </DescList>
            </CardBody>
          </Card>
        )}
      </div>
    </SplitView>
  );
}

function CopyableCode({ value }: { value: string }) {
  return (
    <code
      title="Click to copy"
      onClick={() => void copyToClipboard(value)}
      style={{ fontSize: 'var(--vn-font-size-sm)', cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </code>
  );
}
